import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import type { NewsItem } from "@/mocks/news";
import { relativeTime } from "@/utils/news";

export default function HeroCarousel({ slides }: { slides: NewsItem[] }) {
  const [active, setActive] = useState(0);
  const [paused, setPaused] = useState(false);

  const go = useCallback(
    (next: number) => {
      const total = slides.length;
      setActive((next + total) % total);
    },
    [slides.length],
  );

  useEffect(() => {
    if (paused) return;
    const timer = window.setInterval(() => {
      setActive((prev) => (prev + 1) % slides.length);
    }, 5200);
    return () => window.clearInterval(timer);
  }, [paused, slides.length]);

  const current = slides[active];
  if (!current) return null;

  return (
    <section
      className="relative rounded-lg overflow-hidden bg-foreground-900 h-[420px] md:h-[470px]"
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
      aria-label="焦点爆料轮播"
    >
      {slides.map((item, index) => (
        <div
          key={item.id}
          className={`absolute inset-0 transition-opacity duration-700 ${
            index === active ? "opacity-100" : "opacity-0 pointer-events-none"
          }`}
        >
          <img
            src={item.image}
            alt={item.title}
            title={`${item.title} - 焦点爆料`}
            className="w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-foreground-950/92 via-foreground-950/55 to-foreground-950/15" />
        </div>
      ))}

      <div className="absolute inset-0 flex flex-col justify-end p-5 md:p-9">
        <div className="max-w-[720px]">
          <div className="flex items-center gap-2">
            <span className="inline-flex items-center h-6 px-2 rounded bg-primary-500 text-background-50 text-xs font-bold">
              今日焦点
            </span>
            <span className="inline-flex items-center h-6 px-2 rounded bg-background-50/15 text-background-50 text-xs border border-background-50/30">
              {current.category}
            </span>
            <span className="text-xs text-background-200/80">{relativeTime(current.publishedAt)}</span>
          </div>
          <h2 className="mt-3 text-[24px] md:text-[38px] leading-[1.28] font-black text-background-50">
            {current.title}
          </h2>
          <p className="mt-3 text-sm md:text-[15px] leading-7 text-background-200/90 line-clamp-2-safe max-w-[620px]">
            {current.summary}
          </p>
          <div className="mt-5 flex flex-wrap items-center gap-3">
            <Link
              to={`/detail/${current.id}`}
              className="inline-flex items-center gap-2 h-10 px-5 rounded-md bg-primary-500 text-background-50 text-sm font-medium hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer"
            >
              阅读完整爆料
              <i className="ri-arrow-right-line" />
            </Link>
            <Link
              to={`/category/${current.categorySlug}`}
              className="inline-flex items-center gap-1.5 h-10 px-4 rounded-md border border-background-50/35 text-background-100 text-sm hover:bg-background-50/10 transition-colors whitespace-nowrap cursor-pointer"
            >
              <i className="ri-list-check-2" />
              更多{current.category}
            </Link>
          </div>
        </div>

        <div className="mt-6 flex items-end justify-between gap-4">
          <div className="flex items-center gap-2">
            {slides.map((item, index) => (
              <button
                key={item.id}
                type="button"
                aria-label={`切换到第 ${index + 1} 条爆料`}
                onClick={() => go(index)}
                className={`h-1.5 rounded-full transition-all cursor-pointer ${
                  index === active ? "w-8 bg-primary-500" : "w-4 bg-background-50/45 hover:bg-background-50/70"
                }`}
              />
            ))}
          </div>
          <div className="hidden sm:flex items-center gap-2">
            <span className="font-label text-background-200/80 text-sm mr-1">
              {`${active + 1}`.padStart(2, "0")} / {`${slides.length}`.padStart(2, "0")}
            </span>
            <button
              type="button"
              aria-label="上一条"
              onClick={() => go(active - 1)}
              className="w-9 h-9 flex items-center justify-center rounded-md border border-background-50/35 text-background-50 hover:bg-background-50/15 transition-colors cursor-pointer"
            >
              <i className="ri-arrow-left-s-line" />
            </button>
            <button
              type="button"
              aria-label="下一条"
              onClick={() => go(active + 1)}
              className="w-9 h-9 flex items-center justify-center rounded-md border border-background-50/35 text-background-50 hover:bg-background-50/15 transition-colors cursor-pointer"
            >
              <i className="ri-arrow-right-s-line" />
            </button>
          </div>
        </div>
      </div>
    </section>
  );
}